// src/support/fiscalTimePeriods.ts
//
// The Salesforce tab's time dropdown: Current Fiscal Month / Quarter / Year / All. The fiscal calendar
// is Cisco's own, read from Salesforce `Period` records (the same source the Opportunities date presets
// use), so a "quarter" matches Salesforce's and not a calendar approximation. A chosen period becomes a
// `current_time_filter` value the Salesforce endpoints and the tile engine already understand:
// `all`, or `range:YYYY-MM-DD:YYYY-MM-DD` (inclusive).

import { format } from 'date-fns';

export type TimePeriodId = 'month' | 'quarter' | 'year' | 'all';

export const DEFAULT_TIME_PERIOD: TimePeriodId = 'quarter';

export interface FiscalPeriodRow {
  type: 'Month' | 'Quarter' | 'Year';
  start: string; // YYYY-MM-DD
  end: string; // YYYY-MM-DD, inclusive
}

export interface TimeRange {
  start: string;
  end: string;
}

export const TIME_PERIOD_OPTIONS: Array<{
  id: TimePeriodId;
  label: string;
  /** Short form used in tile titles ("Total Demos — This Fiscal Quarter"). */
  short: string;
  periodType: FiscalPeriodRow['type'] | null;
}> = [
  { id: 'month', label: 'Current Fiscal Month', short: 'This Fiscal Month', periodType: 'Month' },
  { id: 'quarter', label: 'Current Fiscal Quarter', short: 'This Fiscal Quarter', periodType: 'Quarter' },
  { id: 'year', label: 'Current Fiscal Year', short: 'This Fiscal Year', periodType: 'Year' },
  { id: 'all', label: 'All', short: 'All Time', periodType: null },
];

const toIso = (d: Date) => format(d, 'yyyy-MM-dd');

/** The inclusive window of the fiscal period of this type that contains `today`, or null when unknown. */
export function resolveTimeRange(
  id: TimePeriodId,
  today: Date,
  periods: FiscalPeriodRow[],
): TimeRange | null {
  const type = TIME_PERIOD_OPTIONS.find((o) => o.id === id)?.periodType;
  if (!type) return null;
  const day = toIso(today);
  const p = periods.find((x) => x.type === type && x.start <= day && day <= x.end);
  return p ? { start: p.start, end: p.end } : null;
}

/** The `current_time_filter` value for a window (`all` when there is none). */
export const timeFilterParam = (range: TimeRange | null): string =>
  range ? `range:${range.start}:${range.end}` : 'all';

export interface TimeSelection {
  /** Value to send as `current_time_filter`. */
  filter: string;
  /** The window it covers; null for `all`. */
  range: TimeRange | null;
  /** True while the fiscal calendar is still loading — data requests should wait. */
  pending: boolean;
  /** Set when the chosen period couldn't be resolved and the page fell back to All. */
  notice: string | null;
}

/**
 * Turn the dropdown choice into the filter to apply. `All` needs no calendar. A fiscal choice waits for
 * the calendar; if it can't be resolved (Salesforce error, or no such period type) the page falls back to
 * All with a notice rather than silently showing a different window.
 */
export function selectTimeFilter(args: {
  periodId: TimePeriodId;
  today: Date;
  periods: FiscalPeriodRow[];
  status: 'loading' | 'ready' | 'error';
}): TimeSelection {
  const { periodId, today, periods, status } = args;
  if (periodId === 'all') return { filter: 'all', range: null, pending: false, notice: null };
  if (status === 'loading') return { filter: 'all', range: null, pending: true, notice: null };

  const label = TIME_PERIOD_OPTIONS.find((o) => o.id === periodId)?.label ?? 'the selected period';
  const range = status === 'ready' ? resolveTimeRange(periodId, today, periods) : null;
  if (!range) {
    return {
      filter: 'all',
      range: null,
      pending: false,
      notice: `${label} isn't available from Salesforce, so all time is shown.`,
    };
  }
  return { filter: timeFilterParam(range), range, pending: false, notice: null };
}

/**
 * Tile titles hard-code "Current FY" (e.g. "Total Demos Current FY"), which would mislead once the page is
 * scoped to a month or quarter — swap it for the selected period's short name.
 */
export function retitleForPeriod(name: string, short?: string): string {
  if (!short) return name;
  return /\s*Current FY$/i.test(name) ? `${name.replace(/\s*Current FY$/i, '')} \u2014 ${short}` : name;
}
